import { useEffect, useRef, useState } from 'react'
import { Flame, Wind, Droplets, Settings, Home, ArrowRight, Thermometer, Gauge } from 'lucide-react'
import { supabase } from '../config/supabase'

export default function Products() {
  const sectionRef = useRef<HTMLDivElement>(null)
  const [isVisible, setIsVisible] = useState(false)
  const [activeProduct, setActiveProduct] = useState<number | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [categories, setCategories] = useState<any[]>([])
  const [products, setProducts] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      { threshold: 0.1 }
    )

    if (sectionRef.current) {
      observer.observe(sectionRef.current)
    }

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const fetchData = async () => {
      try {
        // 获取产品分类
        const { data: categoriesData, error: categoriesError } = await supabase
          .from('product_categories_s_762b1c2b_1')
          .select('*')
          .eq('is_deleted', 'n')

        if (categoriesError) {
          console.error('Error fetching categories:', categoriesError)
        } else {
          // 处理乱码问题，确保分类名称正确显示
          const processedCategories = (categoriesData || []).map(category => ({
            ...category,
            name: category.name || '未命名分类'
          }))
          setCategories(processedCategories)
        }

        // 获取产品
        const { data: productsData, error: productsError } = await supabase
          .from('products_s_762b1c2b_1')
          .select('*')
          .eq('is_deleted', 'n')

        if (productsError) {
          console.error('Error fetching products:', productsError)
        } else {
          // 为产品添加默认属性，确保与原代码兼容
          const enhancedProducts = (productsData || []).map((product, index) => {
            const icons = [Wind, Droplets, Flame, Home]
            const colors = [
              { color: 'from-blue-500 to-blue-700', bgColor: 'bg-blue-50', iconColor: 'text-blue-600' },
              { color: 'from-cyan-500 to-cyan-700', bgColor: 'bg-cyan-50', iconColor: 'text-cyan-600' },
              { color: 'from-orange-500 to-orange-700', bgColor: 'bg-orange-50', iconColor: 'text-orange-600' },
              { color: 'from-amber-600 to-amber-800', bgColor: 'bg-amber-50', iconColor: 'text-amber-700' },
            ]
            const colorScheme = colors[index % colors.length]
            
            // 解析 specification 字段为 specs 对象
            let specs = product.specs || { power: '30-300kW', temp: '40-80℃', fuel: '生物质颗粒' }
            if (product.specification) {
              const specLines = product.specification.split('\n')
              const parsedSpecs: any = {}
              specLines.forEach((line: string) => {
                const [key, value] = line.split('：')
                if (key && value) {
                  const cleanKey = key.trim()
                  const cleanValue = value.trim()
                  if (cleanKey.includes('功率')) {
                    parsedSpecs.power = cleanValue
                  } else if (cleanKey.includes('温度')) {
                    parsedSpecs.temp = cleanValue
                  } else if (cleanKey.includes('燃料')) {
                    parsedSpecs.fuel = cleanValue
                  }
                }
              })
              if (Object.keys(parsedSpecs).length > 0) {
                specs = parsedSpecs
              }
            }
            
            return {
              ...product,
              icon: icons[index % icons.length],
              image: product.cover_image || `https://trae-api-cn.mchost.guru/api/ide/v1/text_to_image?prompt=${encodeURIComponent(product.name || 'industrial heater')}&image_size=landscape_4_3`,
              features: typeof product.features === 'string' ? product.features.split(',') : (product.features || ['高效节能', '自动控温', '智能恒温保护']),
              specs: specs,
              color: colorScheme.color,
              bgColor: colorScheme.bgColor,
              iconColor: colorScheme.iconColor,
            }
          })
          
          // 按产品编号正序排序（优先使用 product_code 或 model_code 或 id 排序）
          const sortedProducts = enhancedProducts.sort((a, b) => {
            const getSortValue = (product: any) => {
              return product.product_code || product.model_code || product.id || ''
            }
            const valA = getSortValue(a)
            const valB = getSortValue(b)
            
            // 如果是数字类型，按数字比较
            if (!isNaN(Number(valA)) && !isNaN(Number(valB))) {
              return Number(valA) - Number(valB)
            }
            // 否则按字符串比较
            return String(valA).localeCompare(String(valB))
          })
          
          setProducts(sortedProducts)
        }
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  const filteredProducts = selectedCategory === 'all' 
    ? products 
    : products.filter(product => {
        // 查找分类 ID 对应的分类名称，然后与产品的 category 字段比较
        const category = categories.find(cat => cat.id === selectedCategory);
        if (category && product.category === category.name) {
          return true;
        }
        return false;
      })

  return (
    <section id="products" ref={sectionRef} className="py-8 sm:py-16 md:py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className={`text-center mb-12 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-block px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-sm font-semibold mb-4">
            产品中心
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-secondary-900 mb-4">
            全系列<span className="text-gradient">工业炉产品</span>
          </h2>
          <p className="text-lg text-secondary-600 max-w-2xl mx-auto">
            从生物质能源到传统燃料，我们提供全方位的加热解决方案，满足不同场景的供暖需求
          </p>
        </div>

        {/* Product Categories */}
        <div className={`mb-8 overflow-x-auto pb-2 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex gap-2 min-w-max">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${selectedCategory === 'all' ? 'bg-primary-600 text-white' : 'bg-secondary-100 text-secondary-700 hover:bg-secondary-200'}`}
            >
              全部产品
            </button>
            {categories.map((category, index) => {
              const colors = [
                'from-blue-500 to-blue-700',
                'from-cyan-500 to-cyan-700',
                'from-orange-500 to-orange-700',
                'from-amber-600 to-amber-800',
              ]
              const color = colors[index % colors.length]
              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${selectedCategory === category.id ? `bg-gradient-to-r ${color} text-white` : 'bg-secondary-100 text-secondary-700 hover:bg-secondary-200'}`}
                >
                  {category.name}
                </button>
              )
            })}
          </div>
        </div>

        {/* Products Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {loading ? (
            <div className="col-span-full flex justify-center items-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="col-span-full text-center py-16">
              <p className="text-secondary-500">暂无产品数据</p>
            </div>
          ) : (
            filteredProducts.map((product, index) => (
              <div
                key={product.id}
                className={`group relative bg-white rounded-2xl border border-secondary-100 overflow-hidden transition-all duration-500 hover:shadow-2xl hover:shadow-primary-500/10 hover:-translate-y-1 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
                onMouseEnter={() => setActiveProduct(product.id)}
                onMouseLeave={() => setActiveProduct(null)}
              >
                {/* Card Header with Image */}
                <div className="aspect-square relative overflow-hidden">
                  <img
                    src={product.cover_image || `https://trae-api-cn.mchost.guru/api/ide/v1/text_to_image?prompt=${encodeURIComponent(product.name || 'industrial heater')}&image_size=square`}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Card Content */}
                <div className="p-4 sm:p-6">
                  <div className="mb-3 sm:mb-4">
                    <h3 className="text-4vw sm:text-5vw font-bold text-secondary-900 mb-1">{product.name}</h3>
                    <p className="text-xs sm:text-sm text-secondary-400">{product.nameEn}</p>
                  </div>

                  <p className="text-secondary-600 text-xs sm:text-sm mb-4 sm:mb-6 leading-relaxed">
                    {product.description}
                  </p>

                  {/* Features - Only show first 3 on mobile */}
                  <div className="flex flex-wrap gap-1 sm:gap-2 mb-4 sm:mb-6">
                    {product.features.slice(0, 3).map((feature: string, idx: number) => (
                      <span
                        key={idx}
                        className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium ${product.bgColor} ${product.iconColor}`}
                      >
                        {feature}
                      </span>
                    ))}
                    {product.features.length > 3 && (
                      <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium ${product.bgColor} ${product.iconColor}`}>
                        +{product.features.length - 3}
                      </span>
                    )}
                  </div>

                  {/* Specs */}
                  <div className={`p-3 sm:p-4 rounded-xl ${product.bgColor} mb-4 sm:mb-6`}>
                    <div className="grid grid-cols-3 gap-3 sm:gap-4 text-center">
                      {Object.entries(product.specs).slice(0, 3).map(([key, value], idx) => (
                        <div key={idx}>
                          <div className={`text-xs text-secondary-500 mb-1`}>
                            {key === 'power' ? '功率' : key === 'efficiency' ? '效率' : key === 'fuel' ? '燃料' : key === 'airflow' ? '风量' : key === 'temp' ? '温度' : key === 'pressure' ? '压力' : key === 'control' ? '控制' : key === 'material' ? '材质' : key}
                          </div>
                          <div className={`text-xs sm:text-sm font-semibold ${product.iconColor}`}>{String(value)}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* CTA Button */}
                  <a 
                    href={product.category === '颗粒炉' ? '/air-granule-models?category=颗粒炉' : product.category === '水暖炉' ? '/air-granule-models?category=水暖炉' : product.category === '燃木壁炉' ? '/air-granule-models?category=燃木壁炉' : product.category === '燃烧机' ? '/air-granule-models?category=燃烧机' : '#'}
                    className={`w-full flex items-center justify-center gap-2 py-2 sm:py-3 rounded-xl font-semibold transition-all duration-300 group/btn ${
                      activeProduct === product.id
                        ? `bg-gradient-to-r ${product.color} text-white`
                        : 'bg-secondary-100 text-secondary-700 hover:bg-secondary-200'
                    }`}
                  >
                    了解详情
                    <ArrowRight className={`w-4 h-4 transition-transform ${activeProduct === product.id ? 'group-hover/btn:translate-x-1' : ''}`} />
                  </a>
                </div>
              </div>
            ))
          )}
        </div>



        {/* Bottom CTA */}
        <div className={`mt-16 text-center transition-all duration-700 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-8 p-4 sm:p-6 rounded-2xl bg-secondary-50 border border-secondary-100">
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <div className="p-3 rounded-xl bg-primary-100">
                <Thermometer className="w-6 h-6 text-primary-600" />
              </div>
              <div className="text-left">
                <div className="text-sm text-secondary-500">热效率</div>
                <div className="text-4vw sm:text-xl font-bold text-secondary-900">90%+</div>
              </div>
            </div>
            <div className="w-8 h-px sm:w-px sm:h-12 bg-secondary-200" />
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <div className="p-3 rounded-xl bg-primary-100">
                <Gauge className="w-6 h-6 text-primary-600" />
              </div>
              <div className="text-left">
                <div className="text-sm text-secondary-500">功率范围</div>
                <div className="text-4vw sm:text-xl font-bold text-secondary-900">10-2000kW</div>
              </div>
            </div>
            <div className="w-8 h-px sm:w-px sm:h-12 bg-secondary-200" />
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <div className="p-3 rounded-xl bg-primary-100">
                <Settings className="w-6 h-6 text-primary-600" />
              </div>
              <div className="text-left">
                <div className="text-sm text-secondary-500">定制服务</div>
                <div className="text-4vw sm:text-xl font-bold text-secondary-900">支持OEM</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}